import { useFields } from './FieldsContext';
import { elementFieldTypeEnums } from '@dashboard/services/pageFieldTypes';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import usePage from '@dashboard/hooks/usePage';
import { useQueryClient } from '@tanstack/react-query';
import { pageKey } from '@dashboard/lib/queryKeys';
import logException from '@dashboard/services/logException';
import { confirm } from '@dashboard/lib/confirm';
import { useFieldEdit } from './FieldEditContext';

export function useFieldHelpers(fieldId) {
  if (!fieldId) {
    throw new Error('Field ID is required');
  }

  const { page } = usePage();
  const { saveField, deleteField } = usePageQueries();
  const queryClient = useQueryClient();

  const { state: fields, dispatch } = useFields();
  const { setIsAddingField, hasUnsavedChanges, setHasUnsavedChanges } = useFieldEdit();

  const field = fields.find(field => field._id === fieldId || field.key === fieldId);

  async function onSaveField(values) {
    if (
      values.element !== elementFieldTypeEnums.SELECT &&
      values.element !== elementFieldTypeEnums.MULTIPLE_CHOICE
    ) {
      delete values.options;
    }

    if (values.element === 'tax-id') {
      values.type = 'tax-id';
    }

    if (values.element === 'po-number') {
      values.type = 'po-number';
    }

    const { data, error } = await saveField(values, field._id, page._id);

    if (error) {
      return { error };
    }

    dispatch({
      type: 'update',
      item: field,
      values: {
        ...data,
        isEditing: true,
      },
    });

    setHasUnsavedChanges(false);
    setIsAddingField(false);

    queryClient.invalidateQueries({ queryKey: pageKey(page._id) });

    return { error: '', data };
  }

  async function onRemoveField() {
    if (field.isNew) {
      await onCancelAddField();
      return;
    }

    const del = await confirm('Delete this field?', {
      confirm: 'Delete',
    });

    if (!del) return;

    try {
      const response = await deleteField(field._id, page._id);

      if (response === true) {
        dispatch({ type: 'delete', item: field });

        queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
      } else {
        throw new Error();
      }
    } catch (error) {
      logException(error);
      window.alert('Failed to delete field. Try again or contact support');
    }
  }

  function canRemoveField() {
    if (!field) return;

    if (field.type !== 'email') return true;

    const emailFields = fields.find(f => f.type === 'email');

    if (emailFields.length > 1) return true;

    return false;
  }

  async function onCancelAddField() {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    dispatch({ type: 'delete', item: field });
    setHasUnsavedChanges(false);
    setIsAddingField(false);
  }

  function onDirty(dirty) {
    if (dirty && field.isEditing) {
      setHasUnsavedChanges(true);
    }

    if (!dirty) {
      setHasUnsavedChanges(false);
    }
  }

  const showRemoveField = canRemoveField();

  return {
    showRemoveField,
    onSaveField,
    onRemoveField,
    onCancelAddField,
    onDirty,
  };
}
